import { logger } from '@springtree/eva-sdk-core-logger';
import { IEvaServiceCallOptions, EvaEndpoint } from '@springtree/eva-sdk-core-service';
import { IEvaServiceDefinition } from '@springtree/eva-services-core';
import { IEvaReduxStoreDefaultShape } from '../store';
import { createLogic } from 'redux-logic';
import { IEvaServiceResponseAction, createServiceRequestAction, IStateOptions } from '../action';
import { IEvaServiceState } from '../state';

/**
 * The chain direction type values
 */
export type TChainDirection = 'REQUEST'|'RESPONSE';

/**
 * The parameters to build the chain.
 * Chains can be used freely between requests and responses
 *
 * @export
 * @interface IChainOptions
 * @template SVC_TO
 * @template SVC_FROM
 */
export interface IChainOptions<SVC_FROM extends IEvaServiceDefinition, SVC_TO extends IEvaServiceDefinition> {
  from: {
    service: new () => SVC_FROM
    type: TChainDirection;
    stateOptions?: IStateOptions,
  };
  to: {
    service: new () => SVC_TO
    validate?: (state: IEvaServiceState<SVC_FROM>) => boolean;
    transform?: (state: IEvaServiceState<SVC_FROM>) => SVC_TO['request']|void;
    endpoint?: EvaEndpoint;
    serviceOptions?: IEvaServiceCallOptions;
    stateOptions?: IStateOptions,
  };
}

/**
 * Creates a Redux logic to link 2 EVA service actions together
 *
 * @export
 * @template SHAPE The Redux store shape
 * @template SVC_FROM The EVA Service repository definition to link from
 * @template SVC_TO The EVA Service repository definition to link to
 * @param {IChainOptions<SVC_FROM, SVC_TO>} params The chain link configuration
 * @returns {Logic[]}
 */
export function createChainLogic<SHAPE extends IEvaReduxStoreDefaultShape, SVC_FROM extends IEvaServiceDefinition, SVC_TO extends IEvaServiceDefinition>(
  params: IChainOptions<SVC_FROM, SVC_TO>,
) {

  // Determine the state key name either by the service name or the provided
  // custom state key name
  //
  const fromServiceName: keyof SHAPE = (params && params.from && params.from.stateOptions && params.from.stateOptions.stateKey) || new params.from.service().name as any;
  const toServiceName: keyof SHAPE = (params && params.to && params.to.stateOptions && params.to.stateOptions.stateKey) || new params.to.service().name as any;

  const chainLogic = createLogic<SHAPE, IEvaServiceResponseAction<SVC_FROM>, any, any, any, string>({
    type: `${fromServiceName}:${params.from.type}`,
    processOptions: {
      failType: `${toServiceName}:ERROR`,
    },
    async process({ getState, action }, dispatch, done) {

      // Pull the from service state from the store
      //
      const storeState = getState();
      const fromServiceState: IEvaServiceState<SVC_FROM> = storeState[fromServiceName];

      // Check if the caller provided a validate check which may prevent the link from triggering
      //
      if (params.to && params.to.validate && !params.to.validate(fromServiceState)) {
        logger.debug('[EVA:SDK:LOGIC] Not calling due to validation failure', params)();
      } else {
        // For the to service there is only type request
        // A response type makes no sense since that could have been achieved with
        // a reducer
        //
        // Use the endpoint and call options from the original request
        //
        const endpointUrl: string = (params.to && params.to.endpoint) || fromServiceState.endpoint || storeState.endpointUrl;
        const serviceOptions = (params.to && params.to.serviceOptions) || fromServiceState.serviceOptions;

        // Create the request payload using the optionally provided transform function
        // Most services will chain with minimal details being transferred
        //
        const requestPayload = params.to && params.to.transform ? params.to.transform(fromServiceState) : undefined;

        // Create the request action payload
        //
        const requestAction = createServiceRequestAction({
          serviceOptions,
          endpoint: endpointUrl || '',
          service: params.to.service,
          payload: requestPayload,
          stateOptions: { stateKey: toServiceName as string },
          chainContext: action.payload.chainContext,
        });

        logger.debug('[EVA:SDK:LOGIC] Linked request', requestAction)();
        dispatch(requestAction);
      }

      // Redux logic needs this and if you omit it you will get a timeout warning
      //
      done();
    },
  });

  return [
    chainLogic,
  ];
}
